import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from '../services/api';
import { API } from '../services/api';
import { motion } from 'framer-motion';
import {
  ArrowLeft, Plus, Copy, Trash, ShieldCheck,
  Clock, Envelope, Check, Warning, Spinner, Link as LinkIcon
} from '@phosphor-icons/react';

const AdminInvitesPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [copied, setCopied] = useState(null);

  // Form state
  const [formEmail, setFormEmail] = useState('');
  const [formNote, setFormNote] = useState('');
  const [formExpiry, setFormExpiry] = useState(7);
  const [formMaxUses, setFormMaxUses] = useState(1);

  const fetchInvites = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/invites`);
      setInvites(data);
    } catch {
      // Non-admin or error
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/home');
      return;
    }
    fetchInvites();
  }, [user, navigate, fetchInvites]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const payload = {
        expires_in_days: formExpiry,
        max_uses: formMaxUses,
      };
      if (formEmail) payload.email = formEmail;
      if (formNote) payload.note = formNote;

      await axios.post(`${API}/admin/invites`, payload);
      setShowForm(false);
      setFormEmail('');
      setFormNote('');
      setFormExpiry(7);
      setFormMaxUses(1);
      await fetchInvites();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to create invite');
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (id) => {
    if (!window.confirm('Revoke this invite? The link will stop working.')) return;
    try {
      await axios.delete(`${API}/admin/invites/${id}`);
      await fetchInvites();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to revoke');
    }
  };

  const copyLink = (invite) => {
    const base = window.location.origin;
    const url = `${base}/register?invite=${invite.token}`;
    navigator.clipboard.writeText(url);
    setCopied(invite.id);
    setTimeout(() => setCopied(null), 2000);
  };

  const getStatusBadge = (invite) => {
    const s = invite.status;
    if (s === 'revoked') return <span className="text-[10px] font-semibold px-2 py-0.5 rounded bg-red-50 text-red-600 uppercase tracking-wider">Revoked</span>;
    if (s === 'used') return <span className="text-[10px] font-semibold px-2 py-0.5 rounded bg-slate-100 text-slate-500 uppercase tracking-wider">Used</span>;
    if (s === 'expired') return <span className="text-[10px] font-semibold px-2 py-0.5 rounded bg-amber-50 text-amber-600 uppercase tracking-wider">Expired</span>;
    return <span className="text-[10px] font-semibold px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 uppercase tracking-wider">Active</span>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <Spinner size={28} className="animate-spin text-[#235347]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]" style={{ fontFamily: 'Inter, sans-serif' }}>
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/home')} className="text-slate-400 hover:text-slate-600 transition-colors" data-testid="back-to-home">
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Invite Management</h1>
              <p className="text-xs text-slate-400">Create and manage invite links</p>
            </div>
          </div>
          <button
            onClick={() => setShowForm(!showForm)}
            className="bg-[#235347] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#1A3E35] transition-colors flex items-center gap-1.5"
            data-testid="create-invite-btn"
          >
            <Plus size={16} weight="bold" />
            New invite
          </button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-8">
        {/* Create form */}
        {showForm && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white border border-slate-200 rounded-xl p-6 mb-8"
          >
            <h3 className="text-sm font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Create invite</h3>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-slate-500 mb-1 block">Email (optional — locks invite to this email)</label>
                  <input
                    data-testid="invite-email"
                    type="email"
                    value={formEmail}
                    onChange={(e) => setFormEmail(e.target.value)}
                    placeholder="user@example.com"
                    className="w-full border border-slate-200 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-[#235347]/20 focus:border-[#235347] outline-none"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-500 mb-1 block">Note (optional)</label>
                  <input
                    data-testid="invite-note"
                    type="text"
                    value={formNote}
                    onChange={(e) => setFormNote(e.target.value)}
                    placeholder="e.g. Beta tester"
                    className="w-full border border-slate-200 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-[#235347]/20 focus:border-[#235347] outline-none"
                  />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-slate-500 mb-1 block">Expires in (days)</label>
                  <select
                    data-testid="invite-expiry"
                    value={formExpiry}
                    onChange={(e) => setFormExpiry(Number(e.target.value))}
                    className="w-full border border-slate-200 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-[#235347]/20 focus:border-[#235347] outline-none bg-white"
                  >
                    <option value={1}>1 day</option>
                    <option value={3}>3 days</option>
                    <option value={7}>7 days</option>
                    <option value={14}>14 days</option>
                    <option value={30}>30 days</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-slate-500 mb-1 block">Max uses</label>
                  <select
                    data-testid="invite-max-uses"
                    value={formMaxUses}
                    onChange={(e) => setFormMaxUses(Number(e.target.value))}
                    className="w-full border border-slate-200 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-[#235347]/20 focus:border-[#235347] outline-none bg-white"
                  >
                    <option value={1}>1 use</option>
                    <option value={3}>3 uses</option>
                    <option value={5}>5 uses</option>
                    <option value={10}>10 uses</option>
                    <option value={25}>25 uses</option>
                  </select>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowForm(false)} className="text-sm text-slate-500 hover:text-slate-700 px-4 py-2">
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creating}
                  className="bg-[#235347] text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-[#1A3E35] transition-colors disabled:opacity-50 flex items-center gap-1.5"
                  data-testid="submit-invite-btn"
                >
                  {creating ? <Spinner size={14} className="animate-spin" /> : <ShieldCheck size={14} weight="bold" />}
                  Create invite
                </button>
              </div>
            </form>
          </motion.div>
        )}

        {/* Invite list */}
        {invites.length === 0 ? (
          <div className="text-center py-16">
            <LinkIcon size={32} className="text-slate-300 mx-auto mb-3" weight="duotone" />
            <p className="text-slate-500 text-sm">No invites created yet</p>
            <p className="text-slate-400 text-xs mt-1">Create an invite link to add new users</p>
          </div>
        ) : (
          <div className="space-y-3">
            {invites.map((invite, i) => (
              <motion.div
                key={invite.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.04 }}
                className="bg-white border border-slate-200 rounded-xl p-5"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      {getStatusBadge(invite)}
                      {invite.note && <span className="text-xs text-slate-500">{invite.note}</span>}
                    </div>
                    {invite.email && (
                      <div className="flex items-center gap-1.5 mb-1.5">
                        <Envelope size={12} className="text-slate-400" />
                        <span className="text-xs text-slate-600">{invite.email}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-4 text-[11px] text-slate-400">
                      <span className="flex items-center gap-1">
                        <Clock size={11} />
                        Expires: {invite.expires_at ? new Date(invite.expires_at).toLocaleDateString() : 'Never'}
                      </span>
                      <span>Used: {invite.used_count}/{invite.max_uses}</span>
                    </div>
                    {invite.used_by?.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {invite.used_by.map((u, j) => (
                          <div key={j} className="flex items-center gap-1.5">
                            <Check size={10} className="text-emerald-500" />
                            <span className="text-[11px] text-slate-500">{u.email}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    {invite.status === 'active' && (
                      <>
                        <button
                          onClick={() => copyLink(invite)}
                          className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors text-slate-500 hover:text-slate-700"
                          data-testid={`copy-invite-${invite.id}`}
                          title="Copy invite link"
                        >
                          {copied === invite.id ? <Check size={16} className="text-emerald-500" /> : <Copy size={16} />}
                        </button>
                        <button
                          onClick={() => handleRevoke(invite.id)}
                          className="p-2 rounded-lg border border-slate-200 hover:bg-red-50 transition-colors text-slate-400 hover:text-red-500"
                          data-testid={`revoke-invite-${invite.id}`}
                          title="Revoke invite"
                        >
                          <Trash size={16} />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminInvitesPage;
